import React from 'react'
import { NextImage } from '@/components/NextImage'
import classNames from 'classnames'
import { SearchInput } from '@/components/Inputs'
import { useTranslation } from '@/localization/useTranslation'
import { Logo } from './Logo'
import { FavoriteTeamIcon } from './FavoriteTeamIcon'
import { NavItem } from './NavItem'
import { NavDropdown } from './NavDropdown'
import { AuthDropdown } from './AuthDropdown'

import classes from '../styles.module.scss'
import { toPathname } from '../utils'

interface NavItemType {
  route: string
  text: any
  id?: string
}

interface DesktopNavProps {
  collapsedItems: NavItemType[]
  containerRef: React.RefObject<HTMLDivElement | null>
  favoriteTeamIconAlt?: string
  favoriteTeamIconSrc?: string
  hasProfileLogo?: boolean
  hasSearch?: boolean
  isLoggedIn: boolean
  isPendingLogout: boolean
  logoSrc: string
  onLogout: () => void
  onSearch: (value: string) => void
  pathname: string
  profileIconSrc?: string
  profileName?: string
  searchRef: React.RefObject<any>
  searchTerm: string
  visibleItems: NavItemType[]
}

export const DesktopNav: React.FC<DesktopNavProps> = ({
  collapsedItems,
  containerRef,
  favoriteTeamIconAlt,
  favoriteTeamIconSrc,
  hasProfileLogo,
  hasSearch,
  isLoggedIn,
  logoSrc,
  onLogout,
  onSearch,
  pathname,
  profileIconSrc,
  profileName,
  searchRef,
  searchTerm,
  visibleItems,
}) => {
  const { t } = useTranslation()
  return (
    <div
      className={classNames({
        [classes.desktop]: true,
        [classes.desktop__searching]: searchTerm,
      })}
    >
      <div className={classes.left} ref={containerRef}>
        <Logo src={logoSrc} testId="desktop-logo" />
        {favoriteTeamIconSrc && (
          <FavoriteTeamIcon src={favoriteTeamIconSrc} alt={favoriteTeamIconAlt} />
        )}
        <ul className={classes.items}>
          {visibleItems.map((item) => (
            <NavItem
              key={item.route}
              id={item?.id ?? item.route}
              route={item.route}
              text={t(item.text)}
              isActive={pathname === toPathname(item.route)}
              testId="desktop-item"
            />
          ))}
          {collapsedItems.length > 0 && (
            <NavDropdown
              title={t('More')}
              items={collapsedItems.map(item => ({ ...item, text: t(item.text) }))}
              pathname={pathname}
            />
          )}
        </ul>
      </div>
      <div className={classes.right}>
        {hasSearch &&
          <SearchInput
            onChange={onSearch}
            placeholder={t('Search')}
            ref={searchRef}
          />
        }
        {hasProfileLogo && profileIconSrc && (
          <div className={classes.profileLogo}>
            <NextImage
              src={profileIconSrc}
              alt="Logo"
              width={30}
              height={30}
            />
          </div>
        )}
        <div className={classes.items}>
          <AuthDropdown
            isLoggedIn={isLoggedIn}
            profileName={profileName}
            pathname={pathname}
            onLogout={onLogout}
          />
        </div>
      </div>
    </div>
  )
}
